import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/auth';
import { storage } from '../utils/storage';
import { AUTH_CONFIG } from '../config';
import type { UserInfo, LoginRequest, LoginResponse } from '../types/auth';

interface AuthState {
  // 状态
  token: string | null;
  refreshToken: string | null;
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;

  // Actions
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  fetchUserInfo: () => Promise<void>;
  setAuth: (data: LoginResponse) => void;
  clearAuth: () => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // 初始状态
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,

      // 登录
      login: async (data: LoginRequest) => {
        set({ isLoading: true });
        try {
          const response = await authApi.login(data);
          const { user, access_token, refresh_token } = response.data;
          
          // 保存到storage
          storage.set(AUTH_CONFIG.TOKEN_KEY, access_token);
          storage.set(AUTH_CONFIG.REFRESH_TOKEN_KEY, refresh_token);
          
          set({
            token: access_token,
            refreshToken: refresh_token,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // 登出
      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          get().clearAuth();
        }
      },

      // 刷新Token
      refreshAccessToken: async () => {
        const currentRefreshToken = get().refreshToken || storage.get(AUTH_CONFIG.REFRESH_TOKEN_KEY);
        
        if (!currentRefreshToken) {
          get().clearAuth();
          throw new Error('No refresh token available');
        }

        try {
          const response = await authApi.refreshToken({ refresh_token: currentRefreshToken });
          const { access_token, refresh_token } = response.data;
          
          storage.set(AUTH_CONFIG.TOKEN_KEY, access_token);
          storage.set(AUTH_CONFIG.REFRESH_TOKEN_KEY, refresh_token);
          
          set({
            token: access_token,
            refreshToken: refresh_token,
          });
        } catch (error) {
          get().clearAuth();
          throw error;
        }
      },

      // 获取用户信息
      fetchUserInfo: async () => {
        try {
          const response = await authApi.getCurrentUser();
          set({
            user: response.data,
            isAuthenticated: true,
          });
        } catch (error) {
          get().clearAuth();
          throw error;
        }
      },

      // 设置认证信息
      setAuth: (data: LoginResponse) => {
        const { user, access_token, refresh_token } = data;
        
        storage.set(AUTH_CONFIG.TOKEN_KEY, access_token);
        storage.set(AUTH_CONFIG.REFRESH_TOKEN_KEY, refresh_token);
        
        set({
          token: access_token,
          refreshToken: refresh_token,
          user,
          isAuthenticated: true,
        });
      },

      // 清除认证信息
      clearAuth: () => {
        storage.remove(AUTH_CONFIG.TOKEN_KEY);
        storage.remove(AUTH_CONFIG.REFRESH_TOKEN_KEY);
        
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        });
      },

      // 设置 hydration 完成状态
      setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // Zustand v5 正确的 hydration 回调配置
      onRehydrateStorage: () => {
        return (state, error) => {
          if (!error) {
            useAuthStore.setState({ _hasHydrated: true });
          }
        };
      },
    }
  )
);

// 安全后备机制：如果 onRehydrateStorage 未触发，200ms 后强制检查
setTimeout(() => {
  const state = useAuthStore.getState();
  if (!state._hasHydrated) {
    useAuthStore.setState({ _hasHydrated: true });
  }
}, 200);
